/**
 * LabReportsModal
 *
 * Modal for uploading and viewing patient lab reports.
 * Placement: beside the "Patient Details" button in Consultation and CaseSheet headers.
 *
 * Flow:
 *  - Opens with a list of already-uploaded reports (view-only, eye button to preview).
 *  - Click "Upload Report" → pick files → staged list with preview/remove → "Upload All" → done.
 */

import { useState, useCallback } from 'react';
import { Eye, FolderOpen, Loader2, Upload, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/base/Modal';
import { useLabReports } from '@/lib/hooks/useLabReports';
import type { LabReportRecord } from '@/lib/api/labReports';
import {
  ACCEPTED_EXT,
  FileIcon,
  formatDate,
  useLabReportActions,
  ReportPreviewDialog,
  StagedFilesList,
} from './lab-reports-shared';

// --- Props ---

export interface LabReportsModalProps {
  patientId?: string;
  /** Encounter FHIR ID — links uploaded reports to the clinical encounter */
  encounterId?: string;
  /** Appointment FHIR ID — links uploaded reports to the booked appointment */
  appointmentId?: string;
}

// --- Component ---

export function LabReportsModal({ patientId, encounterId, appointmentId }: LabReportsModalProps) {
  const [open, setOpen] = useState(false);
  const [previewReport, setPreviewReport] = useState<LabReportRecord | null>(null);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);

  const { data: reports = [], isLoading: isFetching } = useLabReports(patientId);
  const {
    inputRef,
    isUploading,
    stagedFiles,
    removeStagedFile,
    clearStagedFiles,
    uploadStagedFiles,
    previewStagedFile,
    loadReportUrl,
    onInputChange,
  } = useLabReportActions({
    patientId,
    encounterId,
    appointmentId,
  });

  /** Preview a report — fetches binary lazily if not cached */
  const handleViewReport = useCallback(
    async (report: LabReportRecord) => {
      const cachedUrl = report.url;
      if (cachedUrl) {
        if (report.contentType === 'application/pdf') {
          window.open(cachedUrl, '_blank', 'noopener');
        } else {
          setPreviewReport({ ...report, url: cachedUrl });
        }
        return;
      }
      setLoadingReportId(report.id);
      const url = await loadReportUrl(report);
      setLoadingReportId(null);
      if (!url) return;
      if (report.contentType === 'application/pdf') {
        window.open(url, '_blank', 'noopener');
      } else {
        setPreviewReport({ ...report, url });
      }
    },
    [loadReportUrl]
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 rounded-2xl bg-[#f8f9fb] px-4 py-1.5 font-hanken text-base font-medium leading-5 tracking-[0px] text-[#586695] transition-colors hover:bg-[#e8e9eb]"
      >
        <FolderOpen className="h-[18px] w-[18px] flex-shrink-0 text-[#2E3E6F]" />
        <span>Lab Reports</span>
      </button>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXT}
        multiple
        className="hidden"
        onChange={onInputChange}
      />

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md" hideCloseButton className="overflow-hidden rounded-2xl p-0">
          <DialogHeader className="flex-row items-center justify-between border-b border-[#EAEBF3] px-6 py-4 sm:text-left">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#EEF2FF]">
                <FolderOpen className="h-4 w-4 text-[#061A61]" />
              </div>
              <DialogTitle className="font-serif text-xl font-normal leading-[30px] tracking-[0px] text-[#051858]">
                Lab Reports
              </DialogTitle>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={isUploading || !patientId}
                onClick={() => inputRef.current?.click()}
                className="flex items-center gap-1.5 rounded-lg border border-[#061A61] px-3 py-1.5 font-hanken text-sm font-medium text-[#061A61] transition-colors hover:bg-[#061A61]/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Upload Report
              </button>

              <DialogClose className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8D90A1] transition-colors hover:bg-[#F5F6F9] hover:text-[#42434B]">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          {/* Uploaded reports list (view-only) — hidden when staging files */}
          {stagedFiles.length === 0 && (
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
              <div className="scrollbar-thin max-h-[272px] overflow-y-auto">
                {isFetching ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-[#4B6EE7]" />
                  </div>
                ) : reports.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <FolderOpen className="mb-2 h-8 w-8 text-[#D1DBFD]" />
                    <p className="font-hanken text-sm text-[#8D90A1]">No reports uploaded yet</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {reports.map((report) => (
                      <div
                        key={report.id}
                        className="flex items-center gap-3 rounded-lg border border-[#EAEBF3] bg-white px-3 py-2"
                      >
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#EEF2FF]">
                          <FileIcon contentType={report.contentType} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p
                            className="truncate font-hanken text-sm font-medium text-[#253057]"
                            title={report.fileName}
                          >
                            {report.fileName}
                          </p>
                          <p className="font-hanken text-xs text-[#8D90A1]">
                            {formatDate(report.uploadedAt)}
                          </p>
                        </div>

                        <span className="flex-shrink-0 rounded bg-[#EEF2FF] px-1.5 py-0.5 font-hanken text-[10px] font-semibold uppercase tracking-wide text-[#4B6EE7]">
                          {report.fileType}
                        </span>

                        <button
                          type="button"
                          title="Preview"
                          disabled={loadingReportId === report.id}
                          onClick={() => handleViewReport(report)}
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-[#8D90A1] transition-colors hover:bg-[#EEF2FF] hover:text-[#061A61]"
                        >
                          {loadingReportId === report.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Staged Files for upload */}
          <StagedFilesList
            stagedFiles={stagedFiles}
            isUploading={isUploading}
            onPreview={previewStagedFile}
            onRemove={removeStagedFile}
            onCancel={clearStagedFiles}
            onUploadAll={async () => {
              const ok = await uploadStagedFiles();
              if (ok) setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <ReportPreviewDialog report={previewReport} onClose={() => setPreviewReport(null)} />
    </>
  );
}
